import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/ui/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Phone, Mail, MapPin, Clock, Map, Loader2, Check } from "lucide-react";
import { SERVICE_CATEGORIES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ContactPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success"
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    service_type: "",
    message: "",
    rgpd: false,
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const errs = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = t("contact.error_email");
    if (form.phone && !/^(0[1-9])[0-9]{8}$/.test(form.phone.replace(/\s/g, "")))
      errs.phone = t("contact.error_phone");
    if (form.message.trim().length < 10)
      errs.message = t("contact.error_message");
    return errs;
  };

  const reset = () => {
    setStatus("idle");
    setErrors({});
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      service_type: "",
      message: "",
      rgpd: false,
    });
  };

  const doSubmit = async () => {
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("idle");
      toast.error(t("contact.toast_error"), {
        action: { label: t("contact.retry"), onClick: doSubmit },
      });
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.rgpd) { toast.error(t("contact.toast_rgpd")); return; }
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    doSubmit();
  };

  return (
    <>
      <Seo titleKey="seo.contact_title" descKey="seo.contact_desc" path="/contact" />
      <Toaster position="top-right" richColors />
      <section className="container-x pt-14 pb-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">{t("nav.contact")}</p>
        <h1 className="mt-2 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-slate-900">
          {t("contact.title")}
        </h1>
        <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-3xl leading-relaxed">
          {t("contact.subtitle")}
        </p>
      </section>

      <section className="container-x pb-20 grid lg:grid-cols-12 gap-8 items-start">
        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 rounded-3xl bg-white border border-slate-200 p-10 ring-soft text-center"
            data-testid="contact-success"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 20 }}
              className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 mx-auto"
            >
              <Check className="h-8 w-8 text-brand-green" />
            </motion.div>
            <h2 className="mt-5 font-heading font-extrabold text-2xl text-slate-900">
              {t("contact.success_title")}
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              {t("contact.success_subtitle")}
            </p>
            <a href="tel:+33649502801" className="mt-6 btn-primary inline-flex">
              <Phone className="h-4 w-4" /> 06 49 50 28 01
            </a>
            <button
              onClick={reset}
              className="mt-3 block w-full text-sm text-slate-400 hover:text-brand-green transition"
              data-testid="contact-send-another"
            >
              {t("contact.send_another")}
            </button>
          </motion.div>
        ) : (
          <form
            onSubmit={submit}
            className="lg:col-span-7 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 ring-soft space-y-4"
            data-testid="contact-form"
          >
            <fieldset disabled={status === "loading"} style={{ border: "none", padding: 0, margin: 0 }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fn" className="mb-2 block">{t("contact.form.first_name")}</Label>
                  <Input id="fn" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="contact-first-name" />
                </div>
                <div>
                  <Label htmlFor="ln" className="mb-2 block">{t("contact.form.last_name")}</Label>
                  <Input id="ln" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="contact-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ph" className="mb-2 block">{t("contact.form.phone")}</Label>
                  <Input
                    id="ph"
                    value={form.phone}
                    onChange={(e) => { set("phone", e.target.value); setErrors((er) => ({ ...er, phone: undefined })); }}
                    data-testid="contact-phone"
                  />
                  {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <Label htmlFor="em" className="mb-2 block">{t("contact.form.email")}</Label>
                  <Input
                    id="em"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => { set("email", e.target.value); setErrors((er) => ({ ...er, email: undefined })); }}
                    data-testid="contact-email"
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
              </div>
              <div>
                <Label htmlFor="st" className="mb-2 block">{t("contact.form.service_type")}</Label>
                <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger id="st" data-testid="contact-service" className="w-full">
                    <SelectValue placeholder={t("contact.form.service_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug}>
                        {t(`categories.${cat.slug}.name`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="msg" className="mb-2 block">{t("contact.form.message")}</Label>
                <Textarea
                  id="msg"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => { set("message", e.target.value); setErrors((er) => ({ ...er, message: undefined })); }}
                  data-testid="contact-message"
                />
                {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
              </div>
              <Label className="flex items-start gap-3 cursor-pointer text-sm text-slate-600">
                <Checkbox checked={form.rgpd} onCheckedChange={(v) => set("rgpd", !!v)} data-testid="contact-rgpd" />
                <span>{t("common.rgpd")}</span>
              </Label>
            </fieldset>
            <Button
              type="submit"
              disabled={status === "loading"}
              className="rounded-full bg-brand-green hover:bg-brand-green/90"
              data-testid="contact-submit"
            >
              {status === "loading"
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("contact.sending")}</>
                : t("contact.form.submit")
              }
            </Button>
          </form>
        )}

        <aside className="lg:col-span-5 space-y-4">
          <FadeUp delay={0}>
            <div className="hidden lg:block rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Équipe BIONET SOLUTIONS"
                className="w-full h-52 object-cover"
                loading="lazy"
              />
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="rounded-3xl bg-white border border-slate-200 p-6 ring-soft">
              <h3 className="font-heading font-bold text-xl text-slate-900">{t("contact.aside.title_coords")}</h3>
              <ul className="mt-4 space-y-3 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-brand-green mt-0.5 shrink-0" />
                  <div>
                    <a href="tel:+33649502801" className="text-2xl font-extrabold text-brand-green hover:text-brand-green/80 transition-colors leading-none block">
                      06 49 50 28 01
                    </a>
                    <span className="text-xs text-slate-500 mt-0.5 block">{t("contact.aside.phone_available")}</span>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-brand-blue mt-0.5 shrink-0" />
                  <a href="mailto:contact@bionet-solutions.com" className="hover:text-brand-green break-all">contact@bionet-solutions.com</a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-brand-blue mt-0.5 shrink-0" />
                  <span>64 chemin de Sermenaz, 01700 Neyron</span>
                </li>
              </ul>
            </div>
          </FadeUp>

          <FadeUp delay={0.2}>
            <div className="rounded-3xl bg-white border border-slate-200 p-6 ring-soft">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-brand-green" />
                <h3 className="font-heading font-bold text-slate-900">{t("contact.aside.title_hours")}</h3>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex justify-between">
                  <span>{t("contact.aside.hours_weekday_label")}</span>
                  <span className="font-medium">{t("contact.aside.hours_weekday_value")}</span>
                </li>
                <li className="flex justify-between">
                  <span>{t("contact.aside.hours_saturday_label")}</span>
                  <span className="font-medium">{t("contact.aside.hours_saturday_value")}</span>
                </li>
                <li className="flex justify-between text-brand-green font-semibold">
                  <span>{t("contact.aside.hours_emergency")}</span>
                  <span>{t("contact.aside.hours_emergency_value")}</span>
                </li>
              </ul>
            </div>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="rounded-3xl bg-white border border-slate-200 p-6 ring-soft">
              <div className="flex items-center gap-2 mb-3">
                <Map className="h-4 w-4 text-brand-green" />
                <h3 className="font-heading font-bold text-slate-900">{t("contact.aside.title_zone")}</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t("contact.aside.zone_cities")}</p>
              <p className="text-xs text-brand-green font-semibold mt-2">{t("contact.aside.zone_national")}</p>
            </div>
          </FadeUp>

          <FadeUp delay={0.4}>
            <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-sky-50 border border-slate-200 p-6">
              <div className="font-heading font-bold text-slate-900">{t("contact.aside.urgency_title")}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t("contact.aside.urgency_desc")}</p>
              <a href="tel:+33649502801" className="mt-4 btn-primary inline-flex">
                <Phone className="h-4 w-4" /> 06 49 50 28 01
              </a>
            </div>
          </FadeUp>
        </aside>
      </section>
    </>
  );
};

export default ContactPage;
